import { config as loadEnv } from 'dotenv'
import { existsSync } from 'node:fs'
import { resolve } from 'node:path'
import Fastify from 'fastify'
import cors from '@fastify/cors'
import helmet from '@fastify/helmet'
import rateLimit from '@fastify/rate-limit'
import compress from '@fastify/compress'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore - No types available for fastify-raw-body
import rawBody from 'fastify-raw-body'
import * as Sentry from '@sentry/node'
import { initializeRedis, redis, supportsRateLimitRedis } from './core/database/redis.js'
import { registerModules } from './core/modules.js'
import { errorHandler, notFoundHandler } from './common/handlers/index.js'
import { probeDependencies, summarizeHealth } from './common/utils/health.js'
import { scheduleCacheWarming } from './modules/ai/cache-warmer.js'

const envCandidates = [
  resolve(process.cwd(), '.env'),
  resolve(process.cwd(), '..', '.env'),
  resolve(process.cwd(), '..', '..', '.env')
]

for (const candidate of envCandidates) {
  if (existsSync(candidate)) {
    loadEnv({ path: candidate, override: false })
    break
  }
}

// Initialize Sentry BEFORE everything else
if (process.env.SENTRY_DSN) {
  Sentry.init({
    dsn: process.env.SENTRY_DSN,
    environment: process.env.NODE_ENV || 'development',
    tracesSampleRate: 0.1 // 10% of transactions
  })
}

const isProduction = process.env.NODE_ENV === 'production'
const loggerOptions: Record<string, unknown> = {
  level: process.env.LOG_LEVEL || (isProduction ? 'info' : 'debug')
}

if (!isProduction && process.env.PRETTY_LOGS !== 'false') {
  loggerOptions.transport = {
    target: 'pino-pretty',
    options: {
      translateTime: 'HH:MM:ss Z',
      ignore: 'pid,hostname'
    }
  }
}

const app = Fastify({
  logger: loggerOptions,
  trustProxy: true, // Trust X-Forwarded-* headers from Caddy
  requestIdLogLabel: 'reqId',
  disableRequestLogging: false,
  requestIdHeader: 'x-request-id',
  bodyLimit: 10 * 1024 * 1024, // 10MB max body size
  connectionTimeout: 30000, // 30s connection timeout
  keepAliveTimeout: 65000, // 65s keep-alive (> load balancer timeout)
  pluginTimeout: 30000, // 30s plugin load timeout
  requestTimeout: 60000, // 60s request timeout
  routerOptions: {
    maxParamLength: 200 // Prevent large URL params (Fastify v5 deprecation-safe)
  }
})

// Add support for binary bodies (CRDT sync)
app.addContentTypeParser('application/octet-stream', { parseAs: 'buffer' }, function (_req, body, done) {
  done(null, body)
})

await app.register(rawBody, {
  field: 'rawBody',
  global: false,
  encoding: 'utf8',
  runFirst: true
})

// Global middleware
// Strict CORS allowlist (compare by hostname, not suffix)
const allowAllCors = process.env.CORS_ALLOW_ALL === 'true'
const parseAllowedOrigins = (raw?: string | null) =>
  (raw ?? '')
    .split(',')
    .map(origin => origin.trim())
    .filter(Boolean)

const defaultProdOrigins = [
  'https://roadtripper.io',
  'https://www.roadtripper.io',
  'https://app.roadtripper.io',
  'https://api.roadtripper.io'
]

const defaultDevOrigins = ['http://localhost:5173', 'http://localhost:5174']

const envAllowlist = parseAllowedOrigins(process.env.CORS_ALLOWLIST)

const allowedOrigins = allowAllCors
  ? ['*']
  : envAllowlist.length
    ? envAllowlist
    : isProduction
      ? defaultProdOrigins
      : ['*'] // Allow all origins in development (mobile LAN support)

await app.register(cors, {
  origin: allowAllCors
    ? true
    : (origin, callback) => {
      // Allow requests with no origin (mobile apps, Postman, etc.)
      if (!origin) {
        callback(null, true)
        return
      }

      // Parse and compare using hostname to avoid suffix-based bypasses
      let isAllowed = false
      try {
        const originUrl = new URL(origin)
        const originHost = originUrl.host // includes hostname:port
        isAllowed = allowedOrigins.some(allowed => {
          if (allowed === '*') return true
          try {
            const allowedUrl = new URL(allowed)
            // Exact match on host (hostname[:port]) to prevent crafted suffixes
            return originHost === allowedUrl.host
          } catch {
            // Non-URL entries (dev convenience)
            return origin === allowed
          }
        })
      } catch {
        isAllowed = false
      }

      if (isAllowed) {
        callback(null, true)
      } else {
        app.log.warn({ origin }, 'CORS: Origin not allowed')
        callback(new Error('Not allowed by CORS'), false)
      }
    },
  credentials: true,
  methods: ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'OPTIONS'],
  allowedHeaders: ['Content-Type', 'Authorization', 'x-device-id', 'x-request-id'],
  // Cache preflight OPTIONS responses for 24h (86400s) to reduce requests
  maxAge: 86400
})

await app.register(helmet, {
  contentSecurityPolicy: {
    directives: {
      defaultSrc: ["'self'"],
      styleSrc: ["'self'", "'unsafe-inline'"],
      scriptSrc: ["'self'"],
      imgSrc: ["'self'", 'data:', 'https:'],
      connectSrc: ["'self'", 'https://api.roadtripper.io', 'wss://api.roadtripper.io'],
      fontSrc: ["'self'"],
      objectSrc: ["'none'"],
      mediaSrc: ["'self'"],
      frameSrc: ["'none'"]
    }
  },
  crossOriginEmbedderPolicy: false, // Allow cross-origin resources
  hsts: {
    maxAge: 31536000,
    includeSubDomains: true,
    preload: true
  }
})

// Compress HTTP responses (gzip/brotli) - reduces payload size by ~70%
await app.register(compress, {
  global: true,
  threshold: 1024, // Only compress responses > 1KB
  encodings: ['br', 'gzip'], // Prefer brotli, fallback to gzip
  zlib: {
    level: 6 // Balance between speed and compression
  }
})

const parsePositiveInt = (value: string | undefined, fallback: number) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback
}

const rateLimitMax = parsePositiveInt(process.env.RATE_LIMIT_MAX, isProduction ? 400 : 1500)
const rateLimitWindow = process.env.RATE_LIMIT_WINDOW || '1 minute'
const disableRateLimit = process.env.DISABLE_RATE_LIMIT === 'true'

if (!disableRateLimit) {
  await app.register(rateLimit, {
    max: rateLimitMax,
    timeWindow: rateLimitWindow,
    allowList: ['/health', '/api/health', '/ready'],
    ban: 5, // Ban after 5 violations
    cache: 10000, // Cache 10k entries
    skipOnError: true, // Don't rate limit if Redis is down
    // Use Redis only when the client supports defineCommand (ioredis/node-redis).
    // Upstash REST client is stateless and doesn't implement defineCommand, so we
    // fall back to in-memory rate limit in that case.
    redis: undefined, // DISABLED: causing 'this.redis.rateLimit is not a function' error
    nameSpace: 'rl:', // Redis key prefix
    continueExceeding: true,
    enableDraftSpec: true,
    addHeadersOnExceeding: {
      'x-ratelimit-limit': true,
      'x-ratelimit-remaining': true,
      'x-ratelimit-reset': true
    },
    addHeaders: {
      'x-ratelimit-limit': true,
      'x-ratelimit-remaining': true,
      'x-ratelimit-reset': true,
      'retry-after': true
    }
  })
} else {
  app.log.warn('⚠️ Rate limiting DISABLED (DISABLE_RATE_LIMIT=true)')
}

// Initialize infrastructure
const { initializeFirebase, db } = await import('./core/database/firebase.js')
await initializeFirebase()
await initializeRedis()

// Register gamification event listeners (must be after Firebase and Redis)
const { registerGamificationListeners } = await import('./core/events/gamificationListeners.js')
registerGamificationListeners(db, redis)

// Register fuel regeneration cron job (runs daily at midnight UTC)
const { registerFuelRegenerationJob } = await import('./core/cron/fuelRegeneration.js')
registerFuelRegenerationJob(db, redis)
app.log.info('✅ Fuel regeneration cron job registered (daily at 00:00 UTC)')

// Initialize AI worker (must be after Redis)
// ✅ OPTIMIZATION: Allow disabling AI worker in development to reduce Redis usage
const enableAIWorker = process.env.ENABLE_AI_WORKER !== 'false'
if (enableAIWorker) {
  await import('./core/queue/ai.worker.js')
  app.log.info('✅ AI Worker initialized')
} else {
  app.log.warn('⚠️ AI Worker DISABLED (set ENABLE_AI_WORKER=true to enable)')
}

// Register domain modules
await registerModules(app)

// Register analytics routes
const { analyticsRoutes } = await import('./modules/analytics/analytics.controller.js')
await app.register(analyticsRoutes, { prefix: '/api/analytics' })

// Error handlers (must be registered after routes)
app.setErrorHandler((error, request, reply) => {
  // Send to Sentry if configured
  if (process.env.SENTRY_DSN) {
    Sentry.captureException(error, {
      user: { id: request.user?.uid },
      extra: {
        method: request.method,
        url: request.url,
        body: request.body
      }
    })
  }

  // Use original error handler
  errorHandler(error as Error, request, reply)
})

app.setNotFoundHandler(notFoundHandler)

// Basic health check endpoint (used by load balancers)
app.get('/health', async (_request, reply) => {
  const dependencies = await probeDependencies({
    checkRedis: () => redis.ping(),
    checkFirestore: () => db.collection('_health').limit(1).get()
  })
  const summary = summarizeHealth(dependencies)

  const health = {
    status: summary.status,
    timestamp: Date.now(),
    uptime: process.uptime(),
    environment: process.env.NODE_ENV,
    services: dependencies
  }

  return reply.code(summary.statusCode).send(health)
})

// Dependencies health endpoint (API-level)
app.get('/api/health', async (_request, reply) => {
  const dependencies = await probeDependencies({
    checkRedis: () => redis.ping(),
    checkFirestore: () => db.collection('_health').limit(1).get()
  })
  const summary = summarizeHealth(dependencies)

  return reply.code(summary.statusCode).send({
    status: summary.status,
    redis: dependencies.redis,
    firestore: dependencies.firestore
  })
})

// Readiness probe (for Cloud Run)
app.get('/ready', async (_request, reply) => {
  // Server is ready if it's responding
  return reply.code(200).send({ status: 'ready' })
})

// Start server
const start = async () => {
  try {
    const port = Number(process.env.PORT) || 3000
    await app.listen({ port, host: '0.0.0.0' })
    app.log.info(`🚀 Server running on http://localhost:${port}`)

    // Schedule cache warming for popular routes (3am daily)
    scheduleCacheWarming()

    // Warm up critical caches at startup
    if (isProduction) {
      app.log.info('Warming up caches...')
      try {
        // Pre-fetch common POIs or routes if needed
        await redis.ping() // Ensure Redis connection is hot
        app.log.info('Cache warmup complete')
      } catch (error_) {
        app.log.warn(error_, 'Cache warmup failed (non-critical)')
      }
    }
  } catch (err) {
    app.log.error(err)
    process.exit(1)
  }
}

start()

// Graceful shutdown on SIGTERM/SIGINT
const gracefulShutdown = async (signal: string) => {
  app.log.info(`${signal} received, starting graceful shutdown...`)

  // Stop accepting new connections
  await app.close()

  // Close Redis connection
  try {
    await redis.disconnect()
    app.log.info('Redis disconnected')
  } catch (err) {
    app.log.error(err, 'Error disconnecting Redis')
  }

  app.log.info('Graceful shutdown complete')
  process.exit(0)
}

process.on('SIGTERM', () => gracefulShutdown('SIGTERM'))
process.on('SIGINT', () => gracefulShutdown('SIGINT'))
